import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import {
  AppProvider, Page, Card, DescriptionList, Banner, Layout, Badge, Frame,
  Toast, Spinner, Thumbnail, Link, Form, TextField, PageActions, Button, Select
} from '@shopify/polaris';

import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import { adminClient, userErrorsToFormik } from 'api/index';

import PageSkeleton from './PageSkeleton';
import snakeCase from 'lodash/snakeCase';
import I18n from 'i18n-js';

import PolarisBooleanBadge from '@components/shared/ui/PolarisBooleanBadge';
import GeneralModal from 'components/shared/GeneralModal';

import CustomerDataSection from './components/CustomerDataSection';
import ResourceLogList from '@components/views/orders/components/ResourceLogList';
import CustomerTicketComments from './components/CustomerTicketComments';
import CustomerTicketAdminMedia from './components/CustomerTicketAdminMedia';
import CustomerLaneSection from './components/CustomerLaneSection';
import SupplierCaseSection from './components/SupplierCaseSection';

import RelatedRecordsSection from './components/RelatedRecordsSection';

import { polarisBuildListItems } from 'utils/index';

const propTypes = {
  recordId: PropTypes.number.isRequired,
}

const defaultProps = {

}

const CustomerTicketDetails = (props) => {
  const [toast, setToast] = useState(null);

  // =================== API Hooks ==========================
  const { data, loading, error, refetch } = useQuery(RECORD_GET, {
    fetchPolicy: 'network-only',
    variables: {
      id: props.recordId
    }
  });

  // =======================================================

  // >>> First loading <<<
  // Load skeleton if required data hasn't loaded at least once
  if(!data){
    return <PageSkeleton />
  }

  const customerTicket = data.customerTicket;
  const actionsLoading = loading; // Loader on top of content

  const adminDataItems = polarisBuildListItems([
    // requireItems - Only display if true or when ticket has been approved/rejected meaning requiral has been explicitly set.
    // Not shown from the start to avoid confusion as "No" required can be interpreted as not required but also as not set.
    { name: "approvalKind", hideIfEmpty: true, accessor: (value) => I18n.t(`enum.approval_kind.${snakeCase(value)}`) },
    {
      name: "requireItems", 
      accessor: (value) => <PolarisBooleanBadge value={value} />, 
      hideIf: (value, record) => !record.requireItemsDisplayable // Display if on any status after NEW_TICKET or its equivalent)
    },
    { name: "itemsReceived", accessor: (value) => <PolarisBooleanBadge value={value} /> },
    { name: "brightpearlSalesCreditId", hideIfEmpty: true, accessor: (value, record) => 
      <Link external url={record.brightpearlSalesCreditUrl}>{value}</Link>
    },
    { name: "createdAt", accessor: (value) => I18n.l("time.formats.app_datetime", value) },
  ], customerTicket);

  // Just links to other CustomerTickets related to the same order
  const relatedOrderCustomerTickets = customerTicket.relatedOrderCustomerTickets.map(ticket => {
    return {
      term: ticket.name,
      description: (
        <Link external url={`/customer_tickets/${ticket.displayId}`}>
          { ticket.name } - { I18n.t(`enum.ticket_kind.${snakeCase(ticket.ticketKind)}`) }
        </Link>
      )
    }
  });

  return (
    <div className="polaris-page-customer-ticket-details">
      <Page
        fullWidth
        title={
          <>
            <span>{ customerTicket.name }</span>
            { customerTicket.keyCustomerName &&
              <span className='ml8'><Badge status="info">{ customerTicket.keyCustomerName }</Badge></span>
            }
            <span className='ml8'>{ actionsLoading && <Spinner color='teal' size="small" /> }</span>
          </>
        }
        breadcrumbs={[{ content: I18n.t("app.customer_tickets.title"), url: "/customer_tickets" }]}
      >
        <Layout>
          { !!toast && <Toast content={toast} onDismiss={() => setToast(null)} /> }

          {/* Left section - Customer, Attachments, Logs, etc */}
          <Layout.Section>
            <Card>
              {customerTicket.awaitingPickupLocation &&
                <div className="mt12">
                  <PickupLocationPendingBanner customerTicketId={customerTicket.id} onToast={setToast} />
                </div>
              }

              {/* One predicate, shared with the model, so the page and the backend cannot drift into
                  offering an action the backend refuses. */}
              {customerTicket.receivableAtStore &&
                <div className="mt12">
                  <MarkReceivedAtStoreAction customerTicketId={customerTicket.id} onToast={setToast} onRefetch={refetch} />
                </div>
              }

              {/* Customer data and attachments */}
              <CustomerDataSection customerTicket={customerTicket} />
            </Card>

            {/* The two lanes of the case, same anatomy, resolved independently */}
            <CustomerLaneSection
              customerTicket={customerTicket}
              onToast={setToast}
              onRefetch={refetch}
            />

            <SupplierCaseSection
              customerTicket={customerTicket}
              onToast={setToast}
              onRefetch={refetch}
            />

            <CustomerTicketComments
              customerTicketId={customerTicket.id}
              onToast={setToast}
              as={ ({ children }) =>
                <Card>
                  <Card.Section title="Comentarios">
                    { children }
                  </Card.Section>
                </Card>
              }
            />

            { customerTicket.resourceLogs.length > 0 &&
              <Card>
                <Card.Section title="Logs">
                  <ResourceLogList
                    logs={customerTicket.resourceLogs}
                    isGql
                    withCard={false}
                  />
                </Card.Section>
              </Card>
            }

            {!customerTicket.readOnly && (
              <div className="mt10">
                <TicketCancelButton
                  customerTicket={customerTicket}
                  onToast={setToast}
                  onRefetch={refetch}
                />
              </div>
            )}
          </Layout.Section>

          {/* Right section - Admin and related records info */}
          <Layout.Section secondary>
            <Card>
              <Card.Section title="Admin">
                <DescriptionList
                  items={adminDataItems}
                />
              </Card.Section>
            </Card>

            { relatedOrderCustomerTickets.length > 0 &&
              <Card>
                <Card.Section title="RMA relacionados a la Orden">
                  <DescriptionList
                    items={relatedOrderCustomerTickets}
                  />
                </Card.Section>
              </Card>
            }

            <Card>
              <RelatedRecordsSection customerTicket={customerTicket} />
            </Card>

            <CustomerTicketAdminMedia
              customerTicketId={customerTicket.id}
              onToast={setToast}
              as={ ({ children, primaryFooterAction }) =>
                <Card primaryFooterAction={primaryFooterAction}>
                  <Card.Section title="Admin media">
                    { children }
                  </Card.Section>
                </Card>
              }
            />
          </Layout.Section>
        </Layout>
      </Page>
    </div>
  )
};

const PickupLocationPendingBanner = ({ customerTicketId, onToast }) => {
  const [regenerateLink, { loading }] = useMutation(REGENERATE_PICKUP_LOCATION_LINK, {
    onCompleted: (data) => {
      const { payload, errors } = data.customerTicketRegeneratePickupLocationLink;
      if (payload) {
        navigator.clipboard.writeText(payload)
          .then(() => onToast("Link copiado al portapapeles"))
          .catch(() => onToast("No pudimos copiar el link al portapapeles"));
      } else {
        onToast(errors[0]?.message || I18n.t("messages.one_error"));
      }
    },
    onError: () => onToast(I18n.t("messages.request_error")),
  });

  // Banner's action button in this Polaris version has no loading/disabled visual, so guard
  // against a double-click firing the mutation twice while the first call is still in flight.
  const handleCopyLink = () => {
    if (loading) return;
    regenerateLink({ variables: { id: customerTicketId } });
  };

  return (
    <Banner
      title="Esperando ubicación del cliente"
      status="warning"
      action={{ content: 'Generar y copiar nuevo link', onAction: handleCopyLink }}
    >
      <p>El cliente todavía no ha confirmado dónde retirar el producto.</p>
    </Banner>
  );
};

// Row 1 of the RMA product-tracking automation table - creates the store -> CEDI Task that
// propagate_rma_leg_transition!/register_reception_at_cedi! then advance automatically.
// Store choices come from the real PickupPoint catalog (Unimart's actual retail locations) rather
// than free text - mark_received_at_store! still only takes a plain address string, so we send the
// selected store's name as that string; PickupPoint has no street-address field of its own.
const MarkReceivedAtStoreAction = ({ customerTicketId, onToast, onRefetch }) => {
  const modalRef = useRef(null);
  const [pickupPointName, setPickupPointName] = useState('');
  const [formError, setFormError] = useState(null);

  // The selector lists every store, so one capped page would silently hide the ones past it and the
  // operator could not pick them. Page 1 arrives first and the rest are pulled in behind it.
  const { data: pickupPointsData, fetchMore: fetchMorePickupPoints } = useQuery(
    MARK_RECEIVED_PICKUP_POINTS_QUERY, { client: adminClient, variables: { page: 1 } },
  );
  const loadedPage = pickupPointsData?.pickupPoints?.metadata?.page ?? 0;
  const totalPages = pickupPointsData?.pickupPoints?.metadata?.pages ?? 0;

  useEffect(() => {
    if (loadedPage === 0 || loadedPage >= totalPages) return;

    fetchMorePickupPoints({
      variables: { page: loadedPage + 1 },
      updateQuery: (previous, { fetchMoreResult }) => (
        fetchMoreResult
          ? {
            pickupPoints: {
              ...fetchMoreResult.pickupPoints,
              items: [...previous.pickupPoints.items, ...fetchMoreResult.pickupPoints.items],
            },
          }
          : previous
      ),
    });
  }, [loadedPage, totalPages]);

  const storeOptions = [
    { label: 'Selecciona una tienda...', value: '' },
    ...(pickupPointsData?.pickupPoints?.items ?? [])
      .filter((point) => point.active)
      .map((point) => ({ label: point.name, value: point.name })),
  ];

  const [markReceivedAtStore, { loading }] = useMutation(MARK_RECEIVED_AT_STORE, {
    onCompleted: (data) => {
      const { customerTicket, errors } = data.customerTicketMarkReceivedAtStore;
      if (customerTicket) {
        onToast('Producto marcado como recibido en tienda');
        modalRef.current?.close();
        onRefetch();
      } else {
        setFormError(errors?.[0]?.message || I18n.t('messages.one_error'));
      }
    },
    onError: () => setFormError(I18n.t('messages.request_error')),
  });

  const openModal = () => {
    setPickupPointName('');
    setFormError(null);
    modalRef.current?.open();
  };

  const handleSubmit = () => {
    setFormError(null);
    markReceivedAtStore({ variables: { id: customerTicketId, address: pickupPointName } });
  };

  return (
    <>
      <Banner title="Producto pendiente de recibir en tienda" status="warning">
        <p>Marca el producto como recibido para iniciar su traslado a CEDI.</p>
        <div className="mt12">
          <Button onClick={openModal}>Marcar recibido en tienda</Button>
        </div>
      </Banner>

      <GeneralModal
        ref={modalRef}
        title="Marcar recibido en tienda"
        primaryAction={{
          content: 'Confirmar',
          onAction: handleSubmit,
          loading,
          disabled: !pickupPointName,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => modalRef.current?.close() }]}
      >
        {formError && (
          <div className="mb8">
            <Banner status="critical">{formError}</Banner>
          </div>
        )}
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Select
            label="Tienda"
            options={storeOptions}
            value={pickupPointName}
            onChange={setPickupPointName}
          />
        </Form>
      </GeneralModal>
    </>
  );
};

const MARK_RECEIVED_PICKUP_POINTS_QUERY = gql`
  query MarkReceivedAtStorePickupPoints($page: Int!) {
    pickupPoints(pagination: { page: $page, items: 100 }) {
      items {
        id
        name
        active
      }
      metadata {
        page
        pages
      }
    }
  }
`;

const TicketCancelButton = ({ customerTicket, onToast, onRefetch }) => {
  const [isLoading, setIsLoading] = useState(false);
  const cancellationRef = useRef(null);
  
  return (
    <>
      <PageActions
        secondaryActions={[
          {
            content: "Cancelar solicitud",
            destructive: true,
            loading: isLoading,
            onAction: () => cancellationRef.current?.handleCancelAction()
          }
        ]}
      />
      <CustomerTicketCancellation 
        ref={cancellationRef}
        customerTicket={customerTicket}
        onToast={onToast}
        onRefetch={onRefetch}
        onLoadingChange={setIsLoading}
      />
    </>
  );
};

const CustomerTicketCancellation = React.forwardRef(({ customerTicket, onToast, onRefetch, onLoadingChange }, ref) => {
  const modalRef = useRef(null);
  
  const [formState, setFormState] = useState({
    reason: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [cancelTicket, { loading: loadingCancel }] = useMutation(CANCEL_TICKET, {
    onCompleted: (dataCancel) => {
      const customerTicketUpdated = dataCancel.customerTicketCancel.customerTicket;
      if(customerTicketUpdated?.id){
        onToast(I18n.t("messages.update_successful"));
        modalRef.current?.close();
        onRefetch();
      }
      else{
        console.log("Errors", dataCancel);
        onToast(I18n.t("messages.one_error"));
      }
      setIsSubmitting(false);
      onLoadingChange(false);
    },
    onError: (error) => {
      console.log("Error", error);
      onToast(I18n.t("messages.request_error"));
      setIsSubmitting(false);
      onLoadingChange(false);
    }
  });

  useEffect(() => {
    onLoadingChange(isSubmitting || loadingCancel);
  }, [isSubmitting, loadingCancel, onLoadingChange]);

  const handleCancelAction = () => {
    modalRef.current?.open();
  };

  React.useImperativeHandle(ref, () => ({
    handleCancelAction
  }));

  const handleSubmit = () => {
    setIsSubmitting(true);
    onLoadingChange(true);
    cancelTicket({
      variables: {
        id: customerTicket.id,
        reason: formState.reason
      }
    });
  };

  return (
    <>
      <GeneralModal
        ref={modalRef}
        title="Cancelar solicitud"
        primaryAction={{
          content: "Cancelar",
          onAction: handleSubmit,
          destructive: true,
          loading: isSubmitting || loadingCancel,
        }}
        secondaryActions={[
          { content: "Volver", onAction: () => modalRef.current?.close() },
        ]}
      >
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className='polaris-input-multiline-container mb8'>
            <TextField
              label="Razón de cancelación"
              value={formState.reason}
              onChange={(value) => setFormState({...formState, reason: value})}
              multiline={4}
              placeholder="Indique el motivo de la cancelación..."
            />
          </div>
        </Form>
      </GeneralModal>
    </>
  );
});

const CustomerTicketDetailsWithApollo = (props) => {
  return (
    <ApolloProvider client={adminClient}>
      <AppProvider>
        <Frame>
          <CustomerTicketDetails {...props} />
        </Frame>
      </AppProvider>
    </ApolloProvider>
  )
};

const RECORD_GET = gql`
  fragment TeamData on Team {
    id
    name
  }

  fragment CourierData on Courier{
    id
    name
    team{
      ...TeamData
    }
  }

  query($id: ID!){
    customerTicket(id: $id) {
      id
      name
      customerKind
      providerName
      providerCustomerId
      customerEmail
      customerName
      keyCustomerName
      customerPhone
      providerLineItemId
      moneyProductOriginalTotal
      productTitle
      productSku
      productImageUrl
      ticketKind
      reason
      description
      resolutionPreference
      retrievalMethod
      receivableAtStore
      retrievalNotes
      retrievalDate
      address
      lat
      lon
      province
      canton
      district
      zip
      awaitingPickupLocation
      brightpearlSalesCreditId

      status
      requireItems
      approvalKind
      itemsReceived
      productStatus
      resolutionAction
      laneDisplayId
      pendingAction
      slaConsumedPercent
      slaState
      createdAt
      rejectReason

      sendableToSupplier
      conditionedOnWorkshop
      assignedTo {
        id
        name
      }
      supplierCase {
        id
        displayId
        pendingAction
        slaConsumedPercent
        slaState
        status
        supplierResolution
        blocksCustomerResolution
        supplierCreditNoteReference
        workshopCaseNumber
        creditNoteIssuedOn
        workshopCaseNumberEditable
        notes
        sentAt
        slaDueAt
        workshop {
          id
          name
          contact
        }
        supplier {
          id
          name
        }
        assignedTo {
          id
          name
        }
        resolvable
        creditNotePending
        returnTaskCreatable
        resourceLogs {
          id
          displayMessage
          eventDatetime
          notes
        }
      } # supplierCase

      relatedOrderCustomerTickets {
        id
        name
        ticketKind
        displayId
      }

      media{
        url
        identifier
        contentType
      }

      resourceLogs{
        id
        displayMessage
        eventDatetime
        notes
      }

      relatedDeliveryRecordType
      relatedDeliveryRecordId

      pickupPoint{
        id
        name
        address
        schedule
      }

      displayProviderOrderId
      providerOrderUrl
      brightpearlSalesCreditUrl
      approvable
      rejectable
      escalable
      resolutionActionable
      readOnly
      requireItemsDisplayable

      task{
        id
        name
        status
        createdAt
        adminUrl
        courier{
          ...CourierData
        }
      } # task

      relatedDeliveryRecord{
        ... on Order{
          id
          orderNumber
          adminUrl

          courier{
            ...CourierData
          }
        } # on Order

        ... on DispatchOrder{
          id
          name
          orderNumber

          dispatchControl{
            id
            name
            team{
              ...TeamData
            }
          }
        } # on DispatchOrder
      } # relatedDeliveryRecord
      
    } # customerTicket
  }
`;

const CANCEL_TICKET = gql`
mutation($id: ID!, $reason: String!){
  customerTicketCancel(id: $id, reason: $reason) {
    customerTicket {
      id
    }
    errors {
      key
      message
    }
  }
}
`;

const REGENERATE_PICKUP_LOCATION_LINK = gql`
mutation($id: ID!){
  customerTicketRegeneratePickupLocationLink(id: $id) {
    payload
    errors {
      key
      message
    }
  }
}
`;

const MARK_RECEIVED_AT_STORE = gql`
mutation($id: ID!, $address: String!){
  customerTicketMarkReceivedAtStore(id: $id, address: $address) {
    customerTicket {
      id
      productStatus
    }
    errors {
      key
      message
    }
  }
}
`;

const statusToBannerStatus = {
  "NEW_TICKET": "",
  "REJECTED": "critical",
  "APPROVED": "info",
  "APPROVED_AWAITING_ITEMS": "info",
  "AWAITING_TECHNICAL_VISIT": "info",
  "WAITING_RESOLUTION": "info",
  "PROCESSING_RESOLUTION": "info",
  "ESCALATED": "warning",
  "APPROVED_AND_COMPLETED": "success",
  "ESCALATED": "warning",
}

CustomerTicketDetails.propTypes = propTypes;
CustomerTicketDetails.defaultProps = defaultProps;

export default CustomerTicketDetailsWithApollo;